import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Box } from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import {
  deepCopyObject,
  handleDownloadConversation,
  helperGetIsoDate,
  OmniStringToDate,
} from "../../utils/helpers.js";
import AlertDialog from "../dialogs/alertDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressInteractiveAssessmentDialog from "../dialogs/botpressInteractiveAssessmentDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import BotpressConversationsTable from "../ui/botpressConversationsTable.js";
import OmniDateRangePopover from "../ui/omniDateRangePopover.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressConversations = props => {
  const {
    chats,
    searchChatsRequest,
    chatPagination,
    setSearchChatsDatesRequest,
    chatPageLoading,
    deleteChatRequest,
    loadConversationRequest,
    setRerunConversationIdRequest,
    analyticsBotODM,
    botODM,
    awsGroup,
    userODM,
    conversation,
    conversationFeedback,
    updateChatRequest,
    deleteFeedbackRequest,
    updateFeedbackRequest,
    widgetSize = "large",
    minHeight = "333px",
    variant,
  } = props;
  const isTestVariant = variant === "test";
  const { enqueueSnackbar } = useSnackbar();

  const [localStartDate, setLocalStartDate] = useState(new Date(helperGetIsoDate(29, true, true)));
  const [localEndDate, setLocalEndDate] = useState(new Date(helperGetIsoDate(0, false, true)));

  const [rows, setRows] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [downloadConversation, setDownloadConversation] = useState(null);
  const [confirmUnflag, setConfirmUnflag] = useState(null);

  const performToggleTracking = theOriginal => {
    //this is null initially then false when set
    if (theOriginal.tracking === true) {
      theOriginal.tracking = false;
    } else {
      theOriginal.tracking = true;
    }
    updateChatRequest({ chatRecords: [theOriginal] });
  };

  const toggleTracking = theOriginal => {
    if (theOriginal.tracking === true) {
      //unflagging is destructive-ish, so confirm before removing the flag
      setConfirmUnflag(theOriginal);
    } else {
      //flagging — let the user know what flagging means
      performToggleTracking(theOriginal);
      enqueueSnackbar(
        "This conversation has been flagged for testing. We make sure that it is reviewed and satisfactory before we publish any update to your AI",
        { variant: "info" },
      );
    }
  };

  const onRerunConversation = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      let conversationNumber = conversationRow.id;
      setRerunConversationIdRequest({
        rerunConversationId: conversationId,
        rerunConversationNumber: conversationNumber.toString(),
        rerunFeedbackNumber: null,
        rerunIsTestBot: isTestVariant,
      });

      if (awsGroup === constants.userGroups.Administrator) {
        props.history.push(constants.appPaths.appPathAdminBotpressTestHarness);
      } else if (awsGroup === constants.userGroups.Provider) {
        props.history.push(constants.appPaths.appPathProviderBotpressTestHarness);
      } else if (awsGroup === constants.userGroups.Partner) {
        props.history.push(constants.appPaths.appPathPartnerBotpressTestHarness);
      }

      //switcdh
    } catch (error) {
      console.error("Error getting table rows", error);
    }
  };

  useEffect(() => {
    if (chats != null) {
      setRows(chats);
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats]);

  useEffect(() => {
    if (selectedEntry != null) {
      loadConversationRequest({ conversationId: selectedEntry.conversationId, botODM });
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntry]);

  const onRequestNewPage = async pagination => {
    try {
      if (chatPageLoading) {
        return;
      }
      //add dates right here?
      searchChatsRequest(pagination);
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  const onHandleDeveloperLogs = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      const theURL = `https://app.botpress.cloud/workspaces/${botODM.omniData.workspaceId}/bots/${botODM.omniData.botId}/conversations/${conversationId}/logs`;
      window.open(theURL, "_blank");

      //got to page.
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  const onRangeChange = (newStartDate, newEndDate) => {
    // /chatPagination
    setSearchChatsDatesRequest({ chatStartDate: newStartDate, chatEndDate: newEndDate });
    setLocalStartDate(new Date(OmniStringToDate(newStartDate)));
    setLocalEndDate(new Date(OmniStringToDate(newEndDate)));
  };

  //#region analysis conversation

  const [assessConversationEntry, setAssessConversationEntry] = useState(null);
  const [singleConversation, setSingleConversation] = useState(null);

  useEffect(() => {
    if (assessConversationEntry != null) {
      try {
        const credentialedPayload = {
          botODM,
          conversationId: assessConversationEntry.conversationId,
        };
        BotpressAPI.loadConversation(credentialedPayload).then(returnedConversation => {
          setSingleConversation(returnedConversation);
        });
      } catch (error) {
        console.error("Error loading conversation for assessment", error);
      }
    } else {
      setSingleConversation(null);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessConversationEntry]);

  //#endregion
  const onRefreshTable = () => {
    let newPagination = deepCopyObject(chatPagination);
    newPagination["invalidateCache"] = true;
    onRequestNewPage(newPagination);
  };
  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={isTestVariant ? "Test Conversations" : "History"}
        text={`${chatPagination.totalItems} Conversations`}
      >
        <OmniDateRangePopover
          analyticsStartDate={localStartDate}
          analyticsEndDate={localEndDate}
          onRangeChange={onRangeChange}
        />
      </WidgetTitle>

      <WidgetSurveyContent>
        <BotpressConversationsTable
          data={rows}
          isLoading={chatPageLoading}
          onEditEntry={setSelectedEntry}
          onDownloadEntry={setDownloadConversation}
          onRequestNewPage={onRequestNewPage}
          totalItemCount={chatPagination.totalItems}
          onAssessEntry={analyticsBotODM ? setAssessConversationEntry : null}
          onHandleDeveloperLogs={onHandleDeveloperLogs}
          onRerunConversation={onRerunConversation}
          toggleTracking={toggleTracking}
          onRefreshTable={onRefreshTable}
          refreshTooltipTitle="Refresh conversations"
        />
      </WidgetSurveyContent>

      {/* Skeleton overlay while a page is loading. The table stays mounted
          underneath (it self-fetches on mount/pagination), so we cover it rather
          than unmount it — unmounting would re-trigger the fetch in a loop. */}
      {chatPageLoading && (
        <Box sx={{ position: "absolute", top: "56px", left: 0, right: 0, bottom: 0, zIndex: 2 }}>
          <WidgetSkeleton />
        </Box>
      )}

      {selectedEntry != null && (
        <BotpressConversationDialog
          conversationId={selectedEntry.conversationId}
          conversationNumber={selectedEntry.id}
          conversation={conversation}
          botODM={botODM}
          dialogTitle={`Conversation #${selectedEntry.id}`}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          userODM={userODM}
          open={selectedEntry != null}
          defaultValue={selectedEntry}
          onAccept={() => {
            loadConversationRequest(null);
            setSelectedEntry(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setSelectedEntry(null);
          }}
          onFlag={() => {
            if (selectedEntry.tracking !== true) {
              performToggleTracking(selectedEntry);
              enqueueSnackbar(
                "This conversation has been flagged for testing. We make sure that it is reviewed and satisfactory before we publish any update to your AI",
                { variant: "info" },
              );
            }
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: selectedEntry.id });
            loadConversationRequest(null);
            setSelectedEntry(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
          }}
        />
      )}
      {singleConversation != null && (
        <BotpressInteractiveAssessmentDialog
          userODM={userODM}
          analyticsBotODM={analyticsBotODM}
          open={singleConversation != null}
          singleConversation={singleConversation}
          onAccept={() => {
            setAssessConversationEntry(null);
          }}
          onClose={() => {
            setAssessConversationEntry(null);
          }}
        />
      )}
      {downloadConversation != null && (
        //todo replace this with a new dialog that supports a download format property
        <TextInputDialog
          inputLabel={`File Name`}
          open={downloadConversation}
          dialogTitle={"Download Conversation"}
          dialogMessage={`Please enter a file name for the conversation download`}
          defaultValue={`Conversation_${format(new Date(), "yyMMdd_hhmm")}`}
          inputPlaceholder={"File Name Here"}
          onAccept={theValue => {
            try {
              BotpressAPI.loadConversation({
                botODM,
                conversationId: downloadConversation.conversationId,
              }).then(returnedMessages => {
                handleDownloadConversation(returnedMessages.reverse(), theValue.split(".")[0]);
              });

              setDownloadConversation(null);
            } catch (error) {
              console.log(error);
            }
            setDownloadConversation(null);
          }}
          onClose={() => {
            setDownloadConversation(null);
          }}
        />
      )}
      {confirmUnflag != null && (
        <AlertDialog
          open={confirmUnflag != null}
          dialogTitle={"Confirm Unflag"}
          dialogMessage={"Are you sure you want to unflag this conversation?"}
          onAccept={() => {
            performToggleTracking(confirmUnflag);
            setConfirmUnflag(null);
          }}
          onClose={() => {
            setConfirmUnflag(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = (state, ownProps) => {
  const shared = {
    conversation: state.botpressSession.conversation,
    conversationFeedback: state.botpressSession.conversationFeedback,
    awsGroup: state.userSession.awsGroup,
    userODM: state.userSession.userODM,
    analyticsBotODM: state.botpressSession.analyticsBotODM,
  };

  if (ownProps.variant === "test") {
    return {
      ...shared,
      chats: state.botpressSession.testChats,
      chatPagination: state.botpressSession.testChatPagination,
      chatPageLoading: state.botpressSession.testChatPageLoading,
      botODM: state.botpressSession.testBotODM,
    };
  }

  return {
    ...shared,
    chats: state.botpressSession.chats,
    chatsStartDate: state.botpressSession.chatsStartDate,
    chatsEndDate: state.botpressSession.chatsEndDate,
    chatPagination: state.botpressSession.chatPagination,
    chatPageLoading: state.botpressSession.chatPageLoading,
    botODM: state.botpressSession.botpressBotODM,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const shared = {
    loadConversationRequest: payload =>
      dispatch(BotpressSessionActions.loadConversationRequest(payload)),
    setRerunConversationIdRequest: payload =>
      dispatch(BotpressSessionActions.setRerunConversationIdRequest(payload)),
  };

  if (ownProps.variant === "test") {
    return {
      ...shared,
      searchChatsRequest: payload =>
        dispatch(BotpressSessionActions.searchTestChatsRequest(payload)),
      deleteChatRequest: payload => dispatch(BotpressSessionActions.deleteTestChatRequest(payload)),
      updateChatRequest: payload => dispatch(BotpressSessionActions.updateTestChatRequest(payload)),
      deleteFeedbackRequest: payload =>
        dispatch(BotpressSessionActions.deleteTestFeedbackRequest(payload)),
      updateFeedbackRequest: payload =>
        dispatch(BotpressSessionActions.updateTestFeedbackRequest(payload)),
      setSearchChatsDatesRequest: payload =>
        dispatch(BotpressSessionActions.setSearchTestChatsDatesRequest(payload)),
    };
  }

  return {
    ...shared,
    searchChatsRequest: payload => dispatch(BotpressSessionActions.searchChatsRequest(payload)),
    deleteChatRequest: payload => dispatch(BotpressSessionActions.deleteChatRequest(payload)),
    updateChatRequest: payload => dispatch(BotpressSessionActions.updateChatRequest(payload)),
    deleteFeedbackRequest: payload =>
      dispatch(BotpressSessionActions.deleteFeedbackRequest(payload)),
    updateFeedbackRequest: payload =>
      dispatch(BotpressSessionActions.updateFeedbackRequest(payload)),
    setSearchChatsDatesRequest: payload =>
      dispatch(BotpressSessionActions.setSearchChatsDatesRequest(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressConversations));
